<script setup lang="ts">

import { Ref } from "vue";

import {
  IconLogout,
  IconChevronDown,
  IconSettings,
  IconUser
} from "@iconify-prerendered/vue-carbon";
import { legalTypeToText } from "../utils/utils.ts";
import { storeToRefs } from "pinia";
import MButton from "../components/buttons/MButton.vue";
import AccountBlockClient from "../components/layout/Header/AccountBlockClient.vue";
import BasicDropdown from "../components/basic/BasicDropdown.vue";
import BasicAvatar from "../components/basic/BasicAvatar.vue";
import usePersonStore from "@/store/personStore.ts";
import { PersonDTO } from "@/repositories/dto/user/PersonDTO.ts";
import HeaderLogoutButtomWidget from "@/widgets/HeaderLogoutButtomWidget.vue";
import NotificationsBellWidget from "@/widgets/NotificationsBellWidget.vue";
import LinkButton from "@/components/buttons/LinkButton.vue";
import useClientStore from "@/store/clientStore.ts";
import {getRouteXProfile} from "@/views/Auth/Profile.vue";
import {RouteNames} from "@/utils/constants.ts";
import {getRouteOnboardAccountType} from "@/views/Onboarding/OnboardingAccountType.vue";


const personStore = usePersonStore();
const { person }: { person: Ref<PersonDTO | null> } = storeToRefs(personStore);
const { logout } = personStore;

const clientStore = useClientStore();
const { clients, currentClient, currentClientUuid } = storeToRefs(clientStore);

const menuLinks = [
  {
    icon: IconUser,
    link: getRouteXProfile(),
    name: "Profile"
  },
  {
    icon: IconSettings,
    link: { name: RouteNames.X_SETTINGS },
    name: "Settings"
  }
];
</script>

<template>
  <NotificationsBellWidget />

  <div class="group relative ml-4 flex-shrink-0" v-if="person">
    <BasicDropdown>
      <template v-slot:button>
        <button
          type="button"
          class="relative flex rounded-full text-sm items-center gap-2"
          id="user-menu-button"
          aria-expanded="false"
          aria-haspopup="true"
        >
          <BasicAvatar :client="currentClient" v-if="currentClient" />
          <div v-else class="w-12 h-12 rounded-full bg-[#edeff2] flex justify-center items-center">
            <IconUser class="w-6 h-6 text-[var(--m-dark)]" />
          </div>
          <IconChevronDown class="w-4 h-4 color-red" />
        </button>
      </template>
      <template v-slot:content>
        <div class="w-full">
          <!-- User info header -->
          <div class="mx-auto mt-6 pb-8 border-b border-[var(--m-neutral-400)] flex-col justify-center items-center gap-1 flex text-center font-['Poppins'] leading-[38px]text-[#25262a] text-base/[38px] font-normal">
            <div class="flex items-center">
              <BasicAvatar size="big" :client="currentClient" v-if="currentClient" />
              <div v-else class="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-2xl font-semibold text-gray-600">
                {{ (person?.profile?.firstName || person?.email?.split('@')[0] || 'U')[0].toUpperCase() }}
              </div>
            </div>
            <div class="text-[#25262a] text-base font-medium">
              {{ person?.profile?.firstName || person?.email?.split('@')[0] || 'User' }} {{ person?.profile?.lastName || '' }}
            </div>
            <div class="text-[#757c89] text-sm font-normal">
              {{ currentClient ? legalTypeToText(currentClient?.legalType!) : 'Individual' }}
            </div>
          </div>

          <div
            class="w-full mt-4 pb-4 flex-col justify-around items-start border-b border-[var(--m-neutral-400)] gap-4 inline-flex">
            <RouterLink
              v-for="menuLink of menuLinks"
              :to="menuLink.link"
              class="flex items-center gap-3"
            >
              <component :is="menuLink.icon" class="w-6 h-6" />
              {{ menuLink.name }}
            </RouterLink>
          </div>
          <div
            class="w-full mt-4 pb-4 flex-col border-b border-[var(--m-neutral-400)] gap-4 inline-flex ">
            <AccountBlockClient
              v-for="client of clients"
              :key="client.id"
              :selected="client.id === currentClientUuid"
              :client="client" />

            <MButton kind="dashed" size2="medium" :to="getRouteOnboardAccountType()">
              Add Account
            </MButton>
          </div>

          <div class="w-full justify-center items-center inline-flex">
            <LinkButton @click="logout" class="pt-4">
              Log Out
              <IconLogout class="w-6 h-6 inline" />
            </LinkButton>
          </div>
        </div>
      </template>
    </BasicDropdown>
  </div>

  <HeaderLogoutButtomWidget v-else />
</template>

<style scoped lang="scss">

</style>
